import type { ChangeEvent, ReactNode } from 'react'
import { useId } from 'react'
import { Search } from 'lucide-react'
import { uiScope } from '@/lib/cssScope'
import styles from '@/styles/components/ui/SearchField.module.scss'

export interface SearchFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Uppercase label above the field (same pattern as Select). */
  label?: ReactNode
  /** Minimum width for the shell (e.g. `12rem`, `200px`). */
  minWidth?: string
  id?: string
  type?: 'search' | 'text'
  name?: string
  'aria-label'?: string
  autoComplete?: string
}

/**
 * Single-line search input styled like the Select trigger (border, radius, chevron slot → search icon).
 */
export function SearchField({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  className = '',
  label,
  minWidth,
  id,
  type = 'text',
  name,
  'aria-label': ariaLabel,
  autoComplete = 'off',
}: SearchFieldProps) {
  const uid = useId()
  const inputId = id ?? `${uid}-search`
  const labelId = label ? `${inputId}-label` : undefined

  const root = [uiScope('search-field'), styles.root, className].filter(Boolean).join(' ')

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className={root}>
      {label != null && label !== false ? (
        <label id={labelId} htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      ) : null}
      <div
        className={`${styles.field} ${disabled ? styles.fieldDisabled : ''}`.trim()}
        style={minWidth ? { minWidth } : undefined}
      >
        <Search className={styles.icon} aria-hidden />
        <input
          id={inputId}
          type={type}
          name={name}
          className={styles.input}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-labelledby={label ? labelId : undefined}
          aria-label={ariaLabel}
        />
      </div>
    </div>
  )
}

export default SearchField
